import { Flex, Grid, GridItem } from '@chakra-ui/react';
import { documentAction, translationAction } from 'actions';
import FilePreviewDialog, { PreviewFileState } from 'containers/Summary/FilePreviewDialog';
import { HTTP_CODE_RESPONSE, TEMPORARY } from 'helpers/constant';
import { addTokenURL, getFileExtension } from 'helpers/file';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UploadService } from 'services/upload/upload.service';
import { FileDocument } from 'types/reducers/document';
import { Task } from 'types/reducers/task';
import { getStorageReadBlobToken } from 'utils/blob';
import { v4 as uuidv4 } from 'uuid';

import LanguagesMode from './LanguagesMode';
import SelectFiles from './SelectFiles';
import StartTask from './StartTask';
import TranslatedFiles from './TranslatedFiles';
import TransLationJobs from './TranslationJobs';

const Translator = () => {
  const dispatch = useDispatch();
  const { tasks, current } = useSelector((state) => state.translation);
  const { t } = useTranslation();

  const [_, setIsLoading] = useState<boolean>(false);
  const [isShowingPreviewFile, setIsShowingPreviewFile] = useState(false);
  const [previewFile, setPreviewFile] = useState<PreviewFileState>({});
  const [isToggleSidebar, setIsToggleSidebar] = useState(true);

  const newTask = useMemo(
    () => ({
      id: uuidv4(),
      type: TEMPORARY,
      name: t('common.addNew'),
      last_updated: new Date().toISOString(),
    }),
    [t]
  );

  const isTranslationJobSelecting = Boolean(current && current.task_id);

  const loadData = () => {
    setIsLoading(true);
    dispatch(translationAction.resetData());
    dispatch(translationAction.getTranslationTasks());
    setIsLoading(false);
  };

  const onChangeTask = (item: Task) => {
    dispatch(
      translationAction.updateCurrentTranslationTask({
        task_id: item?.id,
        type: item?.type,
      })
    );
  };

  const addNewTask = () => {
    const temporaryTask = tasks.data.find((task) => task.type === TEMPORARY);
    if (temporaryTask) {
      dispatch(
        translationAction.updateCurrentTranslationTask({
          task_id: temporaryTask.id,
          type: temporaryTask?.type,
        })
      );
      return;
    }
    dispatch(translationAction.addTranslationTask(newTask));
    dispatch(translationAction.clearTemporaryTask());
    dispatch(translationAction.updateTemporaryTaskId(newTask.id));
    onChangeTask(newTask);
  };

  const checkReadTokenValid = (token: string | null) => {
    if (!token) {
      return false;
    }
    const searchParams = new URLSearchParams(token);
    const expiredTime = new Date(searchParams.get('se') as string).getTime();
    const currentTime = new Date().getTime();
    const offsetRefreshTime = Number(process.env.REACT_APP_TIMEOUT_REFRESH_SAS_TOKEN ?? 0) * 1000;

    return expiredTime > currentTime + offsetRefreshTime;
  };

  const onOpenFile = (file: FileDocument) => {
    if (!file.url) {
      return;
    }
    readFile(file);
  };

  const readFile = (file: FileDocument) => {
    if (!checkReadTokenValid(getStorageReadBlobToken())) {
      dispatch(documentAction.getReadToken());
    }
    fetchData(file);
  };

  const fetchData = (file: FileDocument) => {
    const fileUrl = addTokenURL(file?.url, getStorageReadBlobToken());
    setPreviewFile({
      url: fileUrl,
      name: file?.name,
      type: getFileExtension(file?.url),
    });

    (async () => {
      try {
        const res = await UploadService.fetchHeadFile(fileUrl);
        if (
          res.statusText !== 'OK' &&
          (res.status === HTTP_CODE_RESPONSE.UNAUTHORIZED ||
            res.status === HTTP_CODE_RESPONSE.NO_SAS_TOKEN)
        ) {
          readFile(file);
        } else {
          setIsShowingPreviewFile(true);
        }
      } catch (error) {
        console.log(error);
      }
    })();
  };

  const onClose = () => {
    setIsShowingPreviewFile(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <>
      <FilePreviewDialog isOpen={isShowingPreviewFile} file={previewFile} onClose={onClose} />
      <Grid gap={5} templateColumns='repeat(12,1fr)' h='calc(100vh - 150px)'>
        <GridItem w={'100%'} colSpan={isToggleSidebar ? 3 : 1}>
          <TransLationJobs
            onAddNew={addNewTask}
            onChange={onChangeTask}
            isToggleSidebar={isToggleSidebar}
            setIsToggleSidebar={setIsToggleSidebar}
          />
        </GridItem>
        <GridItem w={'100%'} colSpan={3} hidden={current && current.task_id ? false : true}>
          <SelectFiles onOpenFile={onOpenFile} />
        </GridItem>
        <GridItem w={'100%'} colSpan={isToggleSidebar ? 3 : 4} hidden={!isTranslationJobSelecting}>
          <LanguagesMode />
        </GridItem>
        <GridItem w={'100%'} colSpan={isToggleSidebar ? 3 : 4} hidden={!isTranslationJobSelecting}>
          <TranslatedFiles />
        </GridItem>
        <GridItem
          transition='width .05s ease-in-out'
          colSpan={isToggleSidebar ? 9 : 11}
          hidden={isTranslationJobSelecting}
          placeItems={'center'}
        >
          <Flex align={'center'} justify={'center'} w={'100%'} h={'100%'}>
            <StartTask onAddNew={addNewTask} />
          </Flex>
        </GridItem>
      </Grid>
    </>
  );
};

export default Translator;
